import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  Box,
  Grid,
  Input,
  MenuItem,
  Paper,
  Button,
  Select,
  SelectChangeEvent,
  TablePaginationProps,
  Stack,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';

import { Table } from '../../../components/table/table';
import { HeadCell, Row, RowCell } from '../../../components/table/types';

import {
  usePatchMerchantConfigMutation,
  useGetMerchantConfigQuery,
  PatchMerchantConfigDTO,
  useGetMerchantWalletsQuery,
} from '../../../api/controllers/merchant';
import { useGetRateMarkupGroupsQuery } from '../../../api';
import { InfoField, InfoFieldChangeHandler } from '../../../components';
import { MerchantConfigStatus } from '../../../enums/merchant';
import { UTCToDateTime } from '../../../utils';
import { MerchantCoinConfig } from '../MerchantCoinConfig';
import Title from '../../../components/title/Title';

export const MerchantConfig: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { profileUid } = useParams<'profileUid'>();
  const [isEditMode, setIsEditMode] = useState(false);

  // api data
  const {
    data: merchantConfig,
    isLoading: isMerchantConfigLoading,
    refetch,
  } = useGetMerchantConfigQuery({ profileUid: profileUid ?? '' });
  const [updateMerchantConfig, { isLoading: isUpdateMerchantConfigLoading }] =
    usePatchMerchantConfigMutation();
  const { data: rateMarkupGroups } = useGetRateMarkupGroupsQuery({});
  const { data: wallets } = useGetMerchantWalletsQuery({ profileUid: profileUid ?? '' });

  const [editDetailFields, setEditDetailFields] = useState<
    Partial<PatchMerchantConfigDTO['body']> | undefined
  >();
  const [updateFields, setUpdateFields] = useState<
    Partial<PatchMerchantConfigDTO['body']> | undefined
  >();
  const startEdit = () => {
    setIsEditMode(true);
    setEditDetailFields(merchantConfig?.config);
    setUpdateFields({
      status: merchantConfig?.config.status,
      merchant_rate_markup_group_id: Number(merchantConfig?.config.merchant_rate_markup_group_id),
    });
  };
  const endEdit = () => {
    setEditDetailFields(undefined);
    setUpdateFields(undefined);
    setIsEditMode(false);
  };

  const handleUpdateMerchantConfig = async () => {
    if (!updateFields) {
      return;
    }

    const default_link_ttl_hours = updateFields.default_link_ttl_hours;

    const defaultLinkTtlHoursToSend =
      default_link_ttl_hours === undefined
        ? undefined
        : default_link_ttl_hours === null ||
            Number(default_link_ttl_hours) <= 0 ||
            isNaN(Number(default_link_ttl_hours))
          ? null
          : Number(default_link_ttl_hours);
    try {
      await updateMerchantConfig({
        profileUid: profileUid ?? '',
        body: {
          ...updateFields,
          merchant_rate_markup_group_id:
            updateFields.merchant_rate_markup_group_id === -1
              ? null
              : updateFields.merchant_rate_markup_group_id,
          default_link_ttl_hours: defaultLinkTtlHoursToSend,
        },
      }).unwrap();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      endEdit();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const handleEditDetailFields: InfoFieldChangeHandler = (e, notEvent, fieldName) => {
    // @ts-ignore
    const name = notEvent && fieldName ? fieldName : e.target.name;
    // @ts-ignore
    const inputType = e.target.type;
    // @ts-ignore
    const value = notEvent ? e : e.target.value;
    let mappedValue = inputType === 'number' ? Number(value) : value;
    if (typeof value === 'string' && value.length === 0) {
      mappedValue = null;
    }

    setEditDetailFields((prevState) => {
      if (!prevState) {
        return {
          [name]: mappedValue,
        };
      }

      return {
        ...prevState,
        [name]: mappedValue,
      };
    });

    setUpdateFields((prevState) => {
      if (!prevState) {
        return {
          [name]: mappedValue,
        };
      }

      return {
        ...prevState,
        [name]: mappedValue,
      };
    });
  };

  const statusSelectOptions = Object.values(MerchantConfigStatus).map((status) => {
    return (
      <MenuItem key={status} value={status}>
        {t(`common.merchant-config-status.${status}`)}
      </MenuItem>
    );
  });

  const walletMapped = wallets?.wallets.length
    ? [
        { id: -1, value: '', name: t('profile-merchant-config.auto-conversion.none') },
        ...wallets?.wallets?.map(({ uid, coin }) => ({ id: uid, value: uid, name: coin.name })),
      ]
    : [{ id: -1, value: '', name: t('profile-merchant-config.auto-conversion.none') }];

  const merchantWalletSelectOptions = walletMapped.map((wallet) => {
    return (
      <MenuItem key={wallet.id} value={wallet.value}>
        {wallet.name}
      </MenuItem>
    );
  });

  const merchantRateMarkupGroupsMapped = rateMarkupGroups?.rateMarkupGroups.length
    ? [
        { id: -1, value: '' },
        ...rateMarkupGroups?.rateMarkupGroups?.map(({ id, name }) => ({ id, value: name })),
      ]
    : [{ id: -1, value: '' }];

  const merchantRateMarkupGroupsSelectOptions = merchantRateMarkupGroupsMapped.map(
    ({ id, value }) => {
      return (
        <MenuItem key={id} value={id} style={{ minHeight: '36px' }}>
          {value}
        </MenuItem>
      );
    }
  );

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Title>{t('profile-merchant-config.title')}</Title>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
          <Stack direction="column" spacing={1}>
            <InfoField
              name={t('profile-merchant-config.logotype.label')}
              value={undefined}
              formattedValue={
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  {merchantConfig?.config.logo && (
                    <img
                      style={{ height: '200px', width: '200px', borderRadius: '50%' }}
                      src={merchantConfig?.config.logo}
                    />
                  )}
                </Box>
              }
              editProps={{ withEditMode: false, isEditMode: false }}
              isLoading={isMerchantConfigLoading}
              hidden={merchantConfig?.config.status !== MerchantConfigStatus.ENABLED}
              withEmptyValue
            />
            <InfoField
              name={t('profile-merchant-config.status.label')}
              value={t(`common.merchant-config-status.${merchantConfig?.config.status}`)}
              editProps={{ withEditMode: true, isEditMode }}
              inputProps={{
                name: 'status',
                value: editDetailFields?.status ?? '',
                onChange: handleEditDetailFields,
                componentType: 'select',
                selectOptions: statusSelectOptions,
              }}
              isLoading={isMerchantConfigLoading}
              withEmptyValue
            />
            <InfoField
              name={t('profile-details.fees.merchant-rate-markup-group')}
              value={merchantConfig?.config.merchant_rate_markup_group_id}
              formattedValue={
                rateMarkupGroups?.rateMarkupGroups.find(
                  (c) => c.id === merchantConfig?.config.merchant_rate_markup_group_id
                )?.name ?? ''
              }
              editProps={{ withEditMode: true, isEditMode }}
              inputProps={{
                name: 'merchant_rate_markup_group_id',
                value: editDetailFields?.merchant_rate_markup_group_id ?? 0,
                onChange: handleEditDetailFields,
                componentType: 'select',
                selectOptions: merchantRateMarkupGroupsSelectOptions,
              }}
              isLoading={isMerchantConfigLoading}
              withEmptyValue
            />
            <InfoField
              name={t('profile-merchant-config.description.label')}
              value={merchantConfig?.config.description}
              placeholder={t('profile-merchant-config.description.placeholder')}
              editProps={{ withEditMode: true, isEditMode }}
              hidden={merchantConfig?.config.status !== MerchantConfigStatus.ENABLED}
              inputProps={{
                name: 'description',
                value: editDetailFields?.description ?? '',
                onChange: handleEditDetailFields,
                componentType: 'textarea',
              }}
              withEmptyValue
              isLoading={isMerchantConfigLoading}
            />
            <InfoField
              name={t('profile-merchant-config.auto-conversion.label')}
              value={merchantConfig?.config.auto_exchange_wallet_uid}
              hidden={merchantConfig?.config.status !== MerchantConfigStatus.ENABLED}
              formattedValue={
                wallets?.wallets.find(
                  (c) => c.uid === merchantConfig?.config.auto_exchange_wallet_uid
                )?.coin.name ?? t('profile-merchant-config.auto-conversion.none')
              }
              editProps={{ withEditMode: true, isEditMode }}
              inputProps={{
                name: 'auto_exchange_wallet_uid',
                value:
                  editDetailFields?.auto_exchange_wallet_uid ??
                  t('profile-merchant-config.auto-conversion.none'),
                onChange: handleEditDetailFields,
                componentType: 'select',
                selectOptions: merchantWalletSelectOptions,
              }}
              isLoading={isMerchantConfigLoading}
              withEmptyValue
            />
            <InfoField
              name={t('profile-merchant-config.link-expiration.label')}
              value={merchantConfig?.config.default_link_ttl_hours}
              hidden={merchantConfig?.config.status !== MerchantConfigStatus.ENABLED}
              placeholder={t('profile-merchant-config.link-expiration.placeholder')}
              editProps={{ withEditMode: true, isEditMode }}
              inputProps={{
                name: 'default_link_ttl_hours',
                value: editDetailFields?.default_link_ttl_hours ?? null,
                onChange: handleEditDetailFields,
                inputType: 'number',
              }}
              isLoading={isMerchantConfigLoading}
              withEmptyValue
            />
            <InfoField
              name={t('profile-merchant-config.status-reason.label')}
              value={merchantConfig?.config.status_reason}
              editProps={{ withEditMode: true, isEditMode }}
              hidden={merchantConfig?.config.status !== MerchantConfigStatus.ENABLED}
              inputProps={{
                name: 'status_reason',
                value: editDetailFields?.status_reason ?? '',
                onChange: handleEditDetailFields,
              }}
              isLoading={isMerchantConfigLoading}
              withEmptyValue
            />

            <InfoField
              name={t('profile-merchant-config.created-at.label')}
              value={
                merchantConfig?.config.created_at
                  ? UTCToDateTime(merchantConfig?.config.created_at)
                  : '-'
              }
            />

            <InfoField
              name={t('profile-merchant-config.validity.label')}
              hidden={merchantConfig?.config.status !== MerchantConfigStatus.ENABLED}
              value={
                merchantConfig?.config.validity
                  ? UTCToDateTime(merchantConfig?.config.validity)
                  : '-'
              }
            />

            <InfoField
              name={t('profile-merchant-config.updated-at.label')}
              value={
                merchantConfig?.config.row_start
                  ? UTCToDateTime(merchantConfig?.config.row_start)
                  : '-'
              }
              withoutDivider
            />

            {/* <InfoField
            name={t('profile-merchant-config.chanded-by.label')}
            value={merchantConfig?.config.changed_by_user_uuid}
          /> */}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              {!isEditMode && (
                <Button
                  variant="text"
                  startIcon={<EditIcon />}
                  onClick={() => startEdit()}
                  disabled={isMerchantConfigLoading}
                >
                  {t('profile-details.action-btn.edit')}
                </Button>
              )}

              {isEditMode && (
                <>
                  <Button
                    variant="contained"
                    onClick={() => handleUpdateMerchantConfig()}
                    disabled={isMerchantConfigLoading}
                  >
                    {t('profile-details.action-btn.save')}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => endEdit()}
                    disabled={isMerchantConfigLoading}
                  >
                    {t('profile-details.action-btn.cancel')}
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Paper>
        <MerchantCoinConfig />
      </Grid>
    </>
  );
};
